/**
 * <LiveReportHeader> — l'en-tête d'un rapport de séance (molecule).
 *
 * Une seule anatomie pour un seul objet : « Dernier bilan » sur l'accueil (`<LastReportCard>`,
 * `size="sm"`) et chaque carte de l'onglet Rapports (`<LiveReportCard>`, `size="md"`) lisent la
 * même ligne de `GET students/:id/live-reports` et la montrent par cet en-tête.
 *
 * - **1ʳᵉ ligne** : la puce matière · chapitre de chaque chapitre travaillé (`<ChapterBadge>`), et
 *   les chips « Remplacement » / « Rattrapage » quand la séance en porte ;
 * - **dessous** : la capsule du tuteur (sa photo, ses initiales sinon), puis « Jeudi 24 septembre ·
 *   18h00 · avec Camille » — l'année seulement quand ce n'est pas l'année en cours ;
 * - **à droite** : l'anneau d'engagement et son mot (`<EngagementGauge>`), plus petit en `sm`.
 *
 * Rien ne s'invente quand une donnée manque : un rapport V1 (ni chapitre ni score) n'a ni puce ni
 * anneau, un tuteur que la réponse ne nomme pas n'a ni capsule ni « avec ». Aucun tiret orphelin.
 *
 * La ligne du tuteur suit la règle de `<SessionRow>` : UNE ligne quand sa colonne est assez large,
 * deux niveaux sinon — le jour en gras, « 18h00 · avec Camille » dessous. C'est la largeur mesurée
 * de la colonne qui tranche, pas celle de la fenêtre, et on ne coupe jamais au milieu d'une
 * information (« Dimanche 27 septembre · » / « 18h00 », vu à la capture sur un téléphone).
 *
 * **Une séance manquée** (`isAbsent`) se réduit à la ligne du tuteur, grisée : « Absent · Jeudi 17
 * septembre · avec Camille », à l'encre secondaire, la capsule estompée comme sur une séance où
 * l'absence est signalée — sans heure, sans puce, sans anneau.
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { ChapterBadge } from "../../atoms/ChapterBadge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { EngagementGauge } from "../EngagementGauge"
import { type UILiveSessionReport } from "../../../ports/primary/ui/types"
import { formatDayLabel, formatDayTime, formatHour } from "../../../ports/primary/ui/formatters/dateFormatter"
import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"

export type LiveReportHeaderProps = {
    report: Pick<
        UILiveSessionReport,
        "date" | "tutor" | "chapters" | "score" | "isAbsent" | "isRattrapage" | "isReplacement"
    >
    /** `md` (défaut) : une carte de l'onglet Rapports. `sm` : « Dernier bilan », l'anneau plus petit. */
    size?: "sm" | "md"
    style?: StyleProp<ViewStyle>
    testID?: string
}

/** Diamètre de l'anneau : l'accueil le veut plus discret que la liste, où il est l'information. */
export const GAUGE_SIZE = { sm: 44, md: 56 } as const

/** La capsule du tuteur — le plus petit avatar signé du livre. */
export const AVATAR_SIZE = 34

/**
 * Largeur de la colonne de texte à partir de laquelle « Mercredi 30 septembre · 18h00 · avec
 * Camille » (305 px en `text-md` à 15 px, gras puis normal ; 284 px à 14) tient sur une ligne. En dessous — un téléphone, où
 * l'anneau et le chevron prennent leur part —, le jour et le reste passent sur deux niveaux.
 */
export const ONE_LINE_FROM = 330

export function LiveReportHeader(props: LiveReportHeaderProps) {
    const { report, size = "md", style, testID } = props
    const { date, tutor, chapters, score, isAbsent, isRattrapage, isReplacement } = report

    // On démarre sur deux niveaux, jamais sur un.
    const { onLayout: measure, atLeast: oneLine } = useWidthAtLeast(ONE_LINE_FROM)

    const hasTutor = tutor.firstname !== ""
    const withTutor = hasTutor ? `avec ${tutor.firstname}` : null
    const capsule = hasTutor ? (
        <Avatar uri={tutor.avatarUrl} name={tutorShortName(tutor)} size={AVATAR_SIZE} signed dimmed={isAbsent} />
    ) : null

    if (isAbsent) {
        const line = ["Absent", formatDayLabel(date, false, true), withTutor].filter(Boolean).join(" · ")
        return (
            <Row gap="2" align="center" style={[styles.bow, style]} testID={testID}>
                {capsule}
                <Text size="text-sm" weight="semibold" color="text-muted" style={styles.fill}>
                    {line}
                </Text>
            </Row>
        )
    }

    const hasChips = chapters.length > 0 || !!isReplacement || !!isRattrapage

    return (
        <Row gap="3" align="center" style={style} testID={testID}>
            <Stack gap="2" style={styles.fill}>
                {hasChips && (
                    <Row gap="1" align="center" style={styles.wrap}>
                        {chapters.map((chapter, index) => (
                            <ChapterBadge key={`${chapter.id}-${index}`} chapter={chapter} />
                        ))}
                        {isReplacement && <Badge text="Remplacement" variant="warning" iconName="user-friends" />}
                        {isRattrapage && <Badge text="Rattrapage" variant="primary" iconName="redo" />}
                    </Row>
                )}

                <Row gap="2" align="center" style={styles.bow}>
                    {capsule}
                    <View style={styles.fill} onLayout={measure}>
                        {oneLine || withTutor == null ? (
                            <Text size="text-md">
                                <Text size="text-md" weight="bold">
                                    {formatDayTime(date, false, "label", true)}
                                </Text>
                                {withTutor != null && (
                                    <Text size="text-md" color="text-muted">
                                        {` · ${withTutor}`}
                                    </Text>
                                )}
                            </Text>
                        ) : (
                            <>
                                <Text size="text-md" weight="bold">
                                    {formatDayLabel(date, false, true)}
                                </Text>
                                <Text size="text-sm" color="text-muted">
                                    {`${formatHour(date)} · ${withTutor}`}
                                </Text>
                            </>
                        )}
                    </View>
                </Row>
            </Stack>

            {score != null && <EngagementGauge score={score} size={GAUGE_SIZE[size]} />}
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    fill: {
        flex: 1,
        flexShrink: 1,
    },
    wrap: {
        flexWrap: "wrap",
    },
    /** Room for the tutor bow-tie that hangs below the avatar. */
    bow: {
        paddingBottom: 6,
    },
}))
